import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import styles from "./BucketsOfAllocation.module.css";

const formatNumber = (value) => {
  const num = Number(value);

  if (Number.isNaN(num)) {
    return value ?? "";
  }

  return num.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
};

const formatPercentage = (value) => {
  const num = Number(value);

  if (Number.isNaN(num)) {
    return "0";
  }

  return num.toFixed(2);
};

const getSortDirection = (isActive, currentDirection) => {
  if (!isActive) return "ASC";
  return currentDirection === "ASC" ? "DESC" : "ASC";
};

const SortIcon = React.memo(({ isActive, sortDirection }) => {
  if (isActive) {
    if (sortDirection === "ASC") {
      return <ArrowDropUpIcon sx={{ fontSize: 18, color: "#fff" }} />;
    }
    return <ArrowDropDownIcon sx={{ fontSize: 18, color: "#fff" }} />;
  }

  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        opacity: 0.5,
      }}
    >
      <ArrowDropUpIcon
        sx={{
          fontSize: 18,
          marginBottom: "-8px",
          color: "#fff",
        }}
      />
      <ArrowDropDownIcon sx={{ fontSize: 18, color: "#fff" }} />
    </span>
  );
});

SortIcon.propTypes = {
  isActive: PropTypes.bool.isRequired,
  sortDirection: PropTypes.string.isRequired,
};

SortIcon.displayName = "SortIcon";

const handleKeyboardAction = (event, callback) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    callback();
  }
};

const BucketsOfAllocation = ({
  apiData,
  loading,
  loadMoreLoading = false,
  hasMore = false,
  onLoadMore,
  showExpanded,
  sortingData = { sortBy: "BG", sortOrder: "DESC" },
  onSortChange,
}) => {
  const data = apiData?.data || [];

  const observerRef = useRef(null);
  const preloadRowRef = useRef(null);

  const handleSort = (columnKey) => {
    if (!onSortChange) return;

    const isActive = sortingData.sortBy === columnKey;
    const newDirection = getSortDirection(isActive, sortingData.sortOrder);

    onSortChange(columnKey, newDirection);
  };

  const renderSortableHeader = (label, columnKey) => {
    const lines = label.split("<br />");

    return (
      <button
        type="button"
        onClick={() => handleSort(columnKey)}
        onKeyDown={(e) => handleKeyboardAction(e, () => handleSort(columnKey))}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
          color: "inherit",
          width: "100%",
          height: "100%",
        }}
        aria-label={`Sort by ${lines.join(" ")}`}
      >
        <span
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.2,
          }}
        >
          {lines.map((line) => (
            <React.Fragment key={line}>
              {line}
              {line !== lines[lines.length - 1] && <br />}
            </React.Fragment>
          ))}
        </span>
        <SortIcon
          isActive={sortingData.sortBy === columnKey}
          sortDirection={sortingData.sortOrder}
        />
      </button>
    );
  };

  useEffect(() => {
    if (
      loading ||
      loadMoreLoading ||
      !hasMore ||
      !onLoadMore ||
      !preloadRowRef.current
    ) {
      return undefined;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (
          firstEntry.isIntersecting &&
          hasMore &&
          !loading &&
          !loadMoreLoading
        ) {
          onLoadMore();
        }
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(preloadRowRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [data, loading, loadMoreLoading, hasMore, onLoadMore]);

  const preloadIndex = data.length >= 15 ? data.length - 3 : data.length - 1;

  return (
    <div
      className={`${styles.tableContainer} ${
        showExpanded
          ? styles.tableContainerheight
          : styles.tableContainerPopupheight
      }`}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={`${styles.stickyColumn} ${styles.bgColumn}`}>
              {renderSortableHeader("BG", "BG")}
            </th>

            <th className={`${styles.stickyColumn} ${styles.smallCColumn}`}>
              {renderSortableHeader("Small<br />C", "Small C")}
            </th>

            <th className={`${styles.stickyColumn} ${styles.formatColumn}`}>
              {renderSortableHeader("Format", "Format")}
            </th>

            <th>
              {renderSortableHeader("Order Loss<br />Post PDQ", "Order Loss Post PDQ")}
            </th>

            <th>
              {renderSortableHeader("NR<br />At Dest", "NR At Dest")}
            </th>

            <th>
              {renderSortableHeader("Order Loss<br />NR Sufficiency", "Order Loss NR Sufficiency")}
            </th>

            <th>
              {renderSortableHeader("NR Propagation<br />Loss", "NR Propagation Loss")}
            </th>

            <th>
              {renderSortableHeader("NR -<br />Alloc %", "NR - Alloc %")}
            </th>

            <th>
              {renderSortableHeader("Alloc -<br />PDQ %", "Alloc - PDQ %")}
            </th>

            <th>
              {renderSortableHeader("NR -<br />PDQ %", "NR - PDQ %")}
            </th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: 7 }).map((_, rowNumber) => (
              <tr key={`initial-loading-row-${rowNumber + 1}`}>
                {Array.from({ length: 10 }).map((__, cellNumber) => (
                  <td
                    key={`initial-loading-cell-${rowNumber + 1}-${cellNumber + 1}`}
                  >
                    <div className={styles.shimmer}></div>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <>
              {data.map((row, index) => {
                const rowKey = [
                  row["BG"] || "",
                  row["Small C"] || "",
                  row["Format"] || "",
                ].join("-");

                return (
                  <tr
                    key={rowKey}
                    ref={index === preloadIndex ? preloadRowRef : null}
                  >
                    <td className={`${styles.stickyColumn} ${styles.bgColumn}`}>
                      {row["BG"]}
                    </td>

                    <td
                      className={`${styles.stickyColumn} ${styles.smallCColumn}`}
                    >
                      {row["Small C"]}
                    </td>

                    <td
                      className={`${styles.stickyColumn} ${styles.formatColumn}`}
                    >
                      {row["Format"]}
                    </td>

                    <td>{formatNumber(row["Order Loss Post PDQ"])}</td>

                    <td>{formatNumber(row["NR At Dest"])}</td>

                    <td>{formatNumber(row["Order Loss NR Sufficiency"])}</td>

                    <td>{formatNumber(row["NR Propagation Loss"])}</td>

                    <td className={styles.highlightCell}>
                      {formatPercentage(row["NR - Alloc %"])}%
                    </td>

                    <td className={styles.highlightCell}>
                      {formatPercentage(row["Alloc - PDQ %"])}%
                    </td>
                    
                    <td className={styles.highlightCell}>
                      {formatPercentage(row["NR - PDQ %"])}%
                    </td>
                  </tr>
                );
              })}

              {loadMoreLoading &&
                Array.from({ length: 3 }).map((_, rowNumber) => (
                  <tr key={`load-more-row-${rowNumber + 1}`}>
                    {Array.from({ length: 10 }).map((__, cellNumber) => (
                      <td
                        key={`load-more-cell-${rowNumber + 1}-${cellNumber + 1}`}
                      >
                        <div className={styles.shimmer}></div>
                      </td>
                    ))}
                  </tr>
                ))}

              {!loadMoreLoading && data.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      fontSize: "12px",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              )}

              {!loadMoreLoading && data.length > 0 && !hasMore && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "12px",
                      color: "#6B7280",
                    }}
                  >
                    No more records
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

BucketsOfAllocation.propTypes = {
  apiData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
  }),
  loading: PropTypes.bool,
  loadMoreLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  showExpanded: PropTypes.bool,
  sortingData: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }),
  onSortChange: PropTypes.func,
};

BucketsOfAllocation.defaultProps = {
  apiData: {
    data: [],
  },
  loading: false,
  loadMoreLoading: false,
  hasMore: false,
  onLoadMore: undefined,
  showExpanded: false,
  sortingData: {
    sortBy: "BG",
    sortOrder: "DESC",
  },
  onSortChange: undefined,
};

export default BucketsOfAllocation;
